interface StableStringifyOptions {
    // When set, keys whose value is `null` or `undefined` are dropped before
    // serializing. A raw request body omits optional keys while the same content
    // assembled back from columns materializes them as explicit `null`; treating
    // both as "unset" keeps a change-detection hash stable across the
    // body ⇄ column round-trip.
    dropNullishKeys?: boolean;
}

/**
 * JSON.stringify with deterministic key ordering. Useful for hashing or
 * equality-comparing values that have round-tripped through Postgres `jsonb`,
 * which does not preserve insertion order: an object that originated as
 * `{ b: 1, a: 2 }` may come back as `{ a: 2, b: 1 }`, producing a different
 * `JSON.stringify` output even though the value is structurally identical.
 *
 * Arrays preserve their order (semantically meaningful); only object keys are
 * sorted, recursively.
 */
export function stableStringify<T>(
    value: T,
    { dropNullishKeys = false }: StableStringifyOptions = {},
): string {
    return JSON.stringify(value, (_key, val) =>
        val && typeof val === "object" && !Array.isArray(val)
            ? Object.fromEntries(
                  Object.entries(val)
                      .filter(
                          ([, entry]) =>
                              !dropNullishKeys ||
                              (entry !== null && entry !== undefined),
                      )
                      .sort(([a], [b]) => a.localeCompare(b)),
              )
            : val,
    );
}
